import React, { useEffect, useRef, useState } from 'react';

import { MultiLevelMenuDropdownProperties } from '../../interface/shared-component.interface';

import RecursiveDropdown from './components/RecursiveDropdown';

interface CustomMultiLevelDropdownProperties {
  inputLabel?: string;
  valueRequired?: boolean;
  placeholder?: string;
  selectedValue: string | number;
  name: string;
  onChangeValue: (name: string, value: any) => void;
  handleChangeValueToShow: (name: string | number) => void;
  originalValue: MultiLevelMenuDropdownProperties[];
  loading: boolean;
}

/**
 * Function to handle multi-level dropdown
 * @param properties
 * @returns
 */
function CustomMultiLevelDropdown(properties: CustomMultiLevelDropdownProperties) {
  const {
    inputLabel,
    valueRequired,
    name,
    placeholder,
    selectedValue,
    onChangeValue,
    handleChangeValueToShow,
    originalValue,
    loading,
  } = properties;

  const [valueToShow, setValueToShow] = useState<string | number>();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const [multiLevelDropdown, setMultiLevelDropdown] = useState<MultiLevelMenuDropdownProperties[]>(
    [],
  );

  const reference = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<string>('close');
  const [style, setStyle] = useState<any>({});

  /**
   * Function to find label of selected id
   * @param items
   * @param id
   * @returns
   */
  const findLabelById = (items: MultiLevelMenuDropdownProperties[], id: string): string | null => {
    let result: string | null = null;

    items.forEach((item) => {
      if (result) return; // already found, skip further checks

      if (item.id === id) {
        result = item.label;
      } else if (item.hasSubMenu && item.subMenu) {
        const subResult = findLabelById(item.subMenu, id);
        if (subResult) {
          result = subResult;
        }
      }
    });

    return result;
  };

  /**
   * Handle toggle selection
   * @param id
   */
  const toggleSelection = (id: string, isElementSelection: boolean) => {
    if (isElementSelection) {
      setValueToShow(findLabelById(multiLevelDropdown, id) || ''); // There will always be a result label
      onChangeValue(name, id);
      setOpen('close');
      return;
    }

    /**
     * Recursive function
     * @param items
     * @returns
     */
    const toggleRecursive = (
      items: MultiLevelMenuDropdownProperties[],
    ): MultiLevelMenuDropdownProperties[] => {
      return items.map((item) => {
        if (item.id === id) {
          return { ...item, isSelected: !item.isSelected };
        }
        return {
          ...item,
          subMenu: item.subMenu ? toggleRecursive(item.subMenu) : undefined,
        };
      });
    };

    setMultiLevelDropdown(toggleRecursive(multiLevelDropdown));
  };

  /**
   * Function to apply search filter
   * @param items
   * @param keyword
   * @returns
   */
  // const filterMenuTreeByLabel = (
  //   items: MultiLevelMenuDropdownProperties[],
  //   keyword: string,
  // ): MultiLevelMenuDropdownProperties[] => {
  //   return items
  //     .map((item) => {
  //       const hasMatchingLabel = item?.label?.toLowerCase()?.includes(keyword?.toLowerCase());

  //       const filteredSubMenu = item.subMenu ? filterMenuTreeByLabel(item.subMenu, keyword) : [];

  //       if (hasMatchingLabel || filteredSubMenu.length > 0) {
  //         return {
  //           ...item,
  //           subMenu: filteredSubMenu,
  //         };
  //       }

  //       return null;
  //     })
  //     .filter(Boolean) as MultiLevelMenuDropdownProperties[];
  // };

  /**
   * Handle the close th drop down function
   * @param event
   */
  const handleClickOutside = (event: MouseEvent) => {
    if (reference.current && !reference.current.contains(event.target as Node)) {
      setOpen('close');
    }
  };

  /**
   * Get the dropdown position
   */
  const openDropDown = (event: React.MouseEvent<HTMLDivElement>) => {
    const { offsetHeight, offsetWidth } = event.currentTarget;
    if (window.innerHeight > event.clientY + 300) {
      setStyle({
        top: `${inputLabel ? offsetHeight + 30 : offsetHeight}px`,
        width: `${offsetWidth}px`,
      });
    } else {
      setStyle({
        bottom: `${offsetHeight}px`,
        width: `${offsetWidth}px`,
        borderBottomColor: '#6c757d',
        borderTopColor: '#e3e8f1',
        borderRadius: '8px 8px 0 0',
      });
    }
    setOpen('open');
  };

  useEffect((): void => {
    document.addEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounce functionality with 1s delay
  useEffect(() => {
    // Clear existing debounce timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set a new debounce timer
    debounceTimer.current = setTimeout(() => {
      handleChangeValueToShow(valueToShow || ''); // pass the latest value
    }, 1000); // 1 second

    // Cleanup on unmount or valueToShow change
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [valueToShow]);

  useEffect(() => {
    setValueToShow(selectedValue);
  }, [selectedValue]);

  useEffect(() => {
    setMultiLevelDropdown(originalValue);
  }, [originalValue]);

  return (
    <div className="custom-multi-level-dropdown-wrap">
      {inputLabel && (
        <p className="input-label">
          {inputLabel}
          {valueRequired && <span>*</span>}
        </p>
      )}
      <div className="input-selected-value-container" onClick={openDropDown} role="presentation">
        <input
          placeholder={placeholder}
          name={name}
          value={valueToShow}
          onChange={(event) => setValueToShow(event.target.value)}
        />
      </div>
      <div className={`dropdown-items-list ${open}`} ref={reference} style={style}>
        <RecursiveDropdown items={multiLevelDropdown} onSelectToggle={toggleSelection} level={0} />
        {loading && (
          <div className="loader-container">
            <div className="lds-dual-ring" />
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomMultiLevelDropdown;
